import React, { useEffect, useRef, useState } from 'react';
import { IFrameContainer, IFrameHtmlBuilder } from './IFrameContainer';
// --- Add MUI Imports ---
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
// --- End MUI Imports ---

interface IFrameReactContainerProps {
    /** 容器样式 */
    style?: React.CSSProperties;
    /** 容器类名 */
    className?: string;
    /** 要加载的URL */
    url?: string;
    /** 要加载的HTML内容 */
    content?: string;
    urlAsContent?:boolean;
    /** 最小宽度（像素） */
    minWidth?: number;
    /** 最小高度（像素） */
    minHeight?: number;
    /** 最大宽度（像素） */
    maxWidth?: number;
    /** 最大高度（像素） */
    maxHeight?: number;
    /** 加载失败的回调 */
    onError?: (error: Error) => void;
    /** 加载完成的回调 */
    onLoad?: () => void;
    htmlBuilder?: IFrameHtmlBuilder
}

export const IFrameReactContainer: React.FC<IFrameReactContainerProps> = ({
    style,
    className,
    url,
    content,
    urlAsContent,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    onError,
    onLoad,
    htmlBuilder
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const iframeContainerRef = useRef<IFrameContainer | null>(null);
    // Internal loading state for the iframe itself
    const [iframeLoading, setIframeLoading] = useState<boolean>(false); 
    // Internal error state for the iframe itself
    const [iframeError, setIframeError] = useState<Error | null>(null); 
 
    useEffect(()=>{
        return ()=>{
            if(iframeContainerRef.current){
                iframeContainerRef.current.exit()
            }
        }
    }, [])
    useEffect(() => {
        // 创建 IFrameContainer 实例
        if (!iframeContainerRef.current && containerRef.current) {
            iframeContainerRef.current = new IFrameContainer();
            const container = iframeContainerRef.current;
            container.setMinSize(minWidth ?? null, minHeight ?? null);
            container.setMaxSize(maxWidth ?? null, maxHeight ?? null);
            containerRef.current.appendChild(container.getContainer());
        }

        return () => {
            // 清理事件监听
            if (containerRef.current && iframeContainerRef.current) {
                containerRef.current.removeChild(iframeContainerRef.current.getContainer());
                iframeContainerRef.current = null;
            }
        };
    }, [minWidth, minHeight, maxWidth, maxHeight]);

    useEffect(() => {
        const iframeContainer = iframeContainerRef.current;
        if (!iframeContainer) return;

        const loadContentOrUrl = async () => {
            // Reset internal state before loading
            setIframeLoading(true); 
            setIframeError(null);
            try {
                if (content) { // Check for content first
                    iframeContainer.loadContent(content, htmlBuilder);
                    // For srcdoc, loading might be considered finished immediately
                    // or rely on resizer events. Let's tentatively set loading false.
                    setIframeLoading(false); 
                    onLoad?.(); // Call parent onLoad immediately for content
                } else if (url) { // Then check for URL
                    // Pass wrapped callbacks to the actual loadUrl
                    const handleLoad = () => {
                        setIframeLoading(false);
                        setIframeError(null);
                        onLoad?.(); // Call the parent's onLoad
                    };

                    const handleError = (error: Error) => {
                        console.error('%cIFrameReactContainer: handleError (callback from IFrameContainer) triggered.', 'color: red;', error);
                        setIframeLoading(false);
                        setIframeError(error);
                        onError?.(error); // Call the parent's onError
                    };
                    if(urlAsContent){
                        iframeContainer.loadUrlAsContent(url, handleLoad, handleError, htmlBuilder);
                        handleLoad();
                    }else{
                        iframeContainer.loadUrl(url, handleLoad, handleError);
                    }
                    // Note: setIframeLoading(false) is now handled inside the callbacks
                } else {
                    setIframeLoading(false); // No URL or content, stop loading
                }
            } catch (error) { // Catch synchronous errors during setup if any
                console.error('>>> Sync Error during loadContentOrUrl setup:', error);
                if (error instanceof Error) {
                    setIframeLoading(false);
                    setIframeError(error);
                    onError?.(error);
                } else {
                    const unknownError = new Error('An unknown error occurred during setup');
                    setIframeLoading(false);
                    setIframeError(unknownError);
                    onError?.(unknownError);
                }
            }
        };

        loadContentOrUrl();
    }, [url, content, onError, onLoad, iframeContainerRef.current]); 
  
    return (
        <div ref={containerRef} style={style} className={className}>
            {/* Display internal loading/error state */}
            {iframeLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 1 }}>Loading preview...</Typography>
                </Box>
            )}
            {iframeError && !iframeLoading && (
                <Alert severity="error" sx={{ m: 1 }}>Preview Error: {iframeError.message}</Alert>
            )}
            {/* The actual iframe container (managed by IFrameContainer class) is appended here */}
        </div>
    );
};